import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { observer } from "mobx-react-lite";
import { useFloatingAgentLinkNavigation } from "./floating-agent-link-navigation";
import { useFloatingAgentMentionLinkHandler } from "./floating-agent-mention-links";
import { exportFloatingAgentSessionEvents } from "./floating-agent-session-events";
import { FloatingAgentThreadView } from "./floating-agent-thread-view";
import { FloatingAgentConnectedWorkLink } from "./floating-agent-connected-work-link";
import { FloatingAgentVoice } from "./floating-agent-voice";
import type { FloatingAgentVoiceNotice } from "./floating-agent-voice-hold";
import {
  FloatingAgentComposerRegistry,
  useComposerMentions,
} from "./floating-agent-composer-mentions";
import {
  FloatingAgentPanelOpenTarget,
  PANEL_COMPOSER_HINT_HEIGHT_PX,
  PANEL_HEADER_GAP_PX,
  PANEL_HEADER_HEIGHT_PX,
  PANEL_HEIGHT_PX,
  PANEL_SHADOW_GUTTER_PX,
  PANEL_THREAD_SLIDE_MS,
  PANEL_WIDTH_PX,
} from "./floating-agent-constants";
import { useLatchedThreadUnreadAnchor } from "./floating-agent-thread-unread-anchor";
import { useUnreadDividerDismissal } from "./floating-agent-unread-divider-dismissal";
import { FloatingAgentPageStore } from "@/store/pages/FloatingAgentPageStore/FloatingAgentPageStore";
import { useAppStore } from "@/store";
import { useWindowResize } from "@/domains/react/useWindowResize";
import { electronModule } from "@/modules/electron";
import {
  FloatingAgentWindowAnchor,
  RecallPermissionKind,
  type FloatingAgentMemAgentSettingsActionChannel,
} from "@/modules/electron/types";
import {
  MDSLink,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds/components/text";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonVariant,
} from "@/mds/components/buttons";
import { MDSIcon, MDSIconKind, MDSLogo, MDSLogoKind } from "@/mds/components/icon";
import {
  MDSActionMenuDropdown,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
} from "@/mds/components/menu";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import { isMemAgentComposerDraftEmpty } from "@/modules/local-storage/mem-agent-composer-draft";
import type { MemAgentPanelSize } from "@/modules/local-storage/mem-agent-panel-size";
import { windowModule } from "@/modules/window";
import { isWin } from "@/domains/platform/isWin";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSComboboxMention } from "@/mds/components/forms";
import { MDSSession } from "@/mds/components/sessions/session";
import { logger } from "@/modules/logger";
import {
  getDefaultMemAgentHoldToTalkKey,
  getMemAgentHoldToTalkKeyDisplay,
} from "@/modules/mem-agent/hold-to-talk-key";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import {
  IMDSSessionRef,
  MDSComboboxFetchDataHandlerSessionInputFormContext,
  SessionFeature,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

// Resolved at render, not module scope — the env module may not be ready
// (or is partially mocked) when this file is first imported.
const voiceNoticeCopy = (notice: FloatingAgentVoiceNotice): string => {
  if (notice === "mic-denied") {
    return clientEnvModule.isDesktop()
      ? "Mem can't use your microphone — enable it in System Settings to talk."
      : "Mem can't use your microphone — allow it in your browser to talk.";
  }
  if (notice === "transcription-failed") {
    return "A voice message couldn't be transcribed and was discarded.";
  }
  return "Your microphone isn't available right now. You can type instead.";
};

export const shouldShowFloatingAgentUpgradeButton = ({
  hasProactiveAccess,
}: {
  hasProactiveAccess: boolean;
}): boolean => {
  return !hasProactiveAccess;
};

const PANEL_VIEWPORT_MARGIN_PX = 16;
const FULLSCREEN_PANEL_MARGIN_PX = 4;
const SETTINGS_MENU_PANEL_EDGE_CROSS_OFFSET_PX = 40;

export const clampFloatingAgentPanelSize = ({
  requestedSize,
  viewportWidth,
  viewportHeight,
}: {
  requestedSize: MemAgentPanelSize;
  viewportWidth: number;
  viewportHeight: number;
}): MemAgentPanelSize => {
  const clampDimension = ({
    requested,
    defaultSize,
    viewportSize,
  }: {
    requested: number;
    defaultSize: number;
    viewportSize: number;
  }): number => {
    if (viewportSize <= 0) return Math.max(defaultSize, requested);

    const maximumSize = Math.max(1, viewportSize - PANEL_VIEWPORT_MARGIN_PX * 2);
    const minimumSize = Math.min(defaultSize, maximumSize);
    return Math.min(Math.max(requested, minimumSize), maximumSize);
  };

  return {
    width: clampDimension({
      requested: requestedSize.width,
      defaultSize: PANEL_WIDTH_PX,
      viewportSize: viewportWidth,
    }),
    height: clampDimension({
      requested: requestedSize.height,
      defaultSize: PANEL_HEIGHT_PX,
      viewportSize: viewportHeight,
    }),
  };
};

export const FloatingAgentPanel: FC<{
  floatingAgentPage: FloatingAgentPageStore;
  voice: FloatingAgentVoice;
  voiceNotice: FloatingAgentVoiceNotice | null;
  composerRegistryRef: MutableRefObject<FloatingAgentComposerRegistry>;
  /** The peek-bubble message this panel open should land on, if any. */
  openTarget: FloatingAgentPanelOpenTarget | null;
  /** The main window's foregrounded notes, attached as implicit mentions. */
  foregroundMentions: MDSComboboxMention[];
  /**
   * "floating" is the web overlay and the standalone desktop window;
   * "docked" is the panel snapped inside the desktop app;
   * "fullscreen" is the phone-width web treatment — the panel takes the
   * whole viewport and sizes the session list from the measured window.
   *
   * Both in-app surfaces (web "floating" and desktop "docked") drag-resize
   * from the 400×540 default and remember the size. The standalone desktop
   * window fills its OS window, which the OS itself resizes.
   */
  variant?: "floating" | "docked" | "fullscreen";
  onDismissVoiceNotice: () => void;
  onCollapse: () => void;
  /** Discards the in-flight capture (the composer's × while recording). */
  onCancelVoice: () => void;
  /** Sends the in-flight capture (the composer's ✓ while recording). */
  onConfirmVoice: () => void;
}> = observer(
  ({
    floatingAgentPage,
    voice,
    voiceNotice,
    composerRegistryRef,
    openTarget,
    foregroundMentions,
    variant = "floating",
    onDismissVoiceNotice,
    onCollapse,
    onCancelVoice,
    onConfirmVoice,
  }) => {
    const { store } = useAppStore();
    const sessionRef = useRef<IMDSSessionRef>(null);
    const handleClickLink = useFloatingAgentLinkNavigation();
    const handleMentionLink = useFloatingAgentMentionLinkHandler();
    const [isOpeningUpgrade, setIsOpeningUpgrade] = useState(false);
    const [settingsMenuIsOpen, setSettingsMenuIsOpen] = useState(false);
    const [requestedPanelSize, setRequestedPanelSize] = useState<MemAgentPanelSize>(
      () =>
        localStorageModule.readMemAgentPanelSize() ?? {
          width: PANEL_WIDTH_PX,
          height: PANEL_HEIGHT_PX,
        }
    );
    // The draft lives only in the mounted editor, and the panel unmounts on
    // collapse, docked/floating handoffs, and widget window reboots
    // (MEM-19041). Persist it as it changes and seed the editor once on
    // mount. Ordinary re-renders retain the same state, while an explicit
    // replacement request can update an already-mounted composer.
    const [composerDraftHtml, setComposerDraftHtml] = useState(() =>
      localStorageModule.readMemAgentComposerDraft()
    );
    const lastPersistedComposerDraftRef = useRef<string | null>(composerDraftHtml);
    const composerHasSeenContentRef = useRef(false);

    useEffect(
      () =>
        localStorageModule.subscribeMemAgentComposerDraftRequests(html => {
          lastPersistedComposerDraftRef.current = html;
          // Ignore an empty editor notification that can arrive before the
          // requested content has been applied.
          composerHasSeenContentRef.current = false;
          setComposerDraftHtml(html);
        }),
      []
    );

    const handleComposerContentHtmlChanged = useCallback((html: string) => {
      if (isMemAgentComposerDraftEmpty(html)) {
        // The editor reports an empty document on mount and after the
        // post-send clear, before any restored draft is applied. Only a
        // deletion after real content clears the stored draft.
        if (!composerHasSeenContentRef.current) return;
        composerHasSeenContentRef.current = false;
        if (lastPersistedComposerDraftRef.current === null) return;
        lastPersistedComposerDraftRef.current = null;
        localStorageModule.clearMemAgentComposerDraft();
        return;
      }
      composerHasSeenContentRef.current = true;
      if (html === lastPersistedComposerDraftRef.current) return;
      lastPersistedComposerDraftRef.current = html;
      localStorageModule.writeMemAgentComposerDraft(html);
    }, []);
    const windowDragGestureRef = useRef<{
      pointerId: number;
      cursorOffsetX: number;
      cursorOffsetY: number;
      startedAtMs: number;
    } | null>(null);
    const resizeGestureRef = useRef<{
      pointerId: number;
      corner: ResizeCorner;
      clientX: number;
      clientY: number;
      screenX: number;
      screenY: number;
      size: MemAgentPanelSize;
      startedAtMs: number;
    } | null>(null);

    const theme = useTheme();
    const isFullscreen = variant === "fullscreen";
    // Phone-width web has no PTT and no desktop app to send people to. The
    // desktop app keeps its hold-to-talk hint at every width — its window
    // narrows past the phone breakpoint (minWidth 400 on macOS), which would
    // otherwise drop a hint for a shortcut that still works.
    const showComposerHint = !isFullscreen || clientEnvModule.isDesktop();
    const fillsDesktopWindow = clientEnvModule.isDesktop() && variant === "floating";
    const collapseIconKind = isFullscreen ? MDSIconKind.Exit : MDSIconKind.WindowMinimize;
    // In-app panels update their own CSS dimensions. The standalone desktop
    // panel uses the same visible handle, but resizes its BrowserWindow over
    // IPC so the shadow gutter remains outside the requested content size.
    const isInAppResizable = !isFullscreen && !fillsDesktopWindow;
    const showsResizeHandle = !isFullscreen;
    // MDSSession needs a pixel height; fullscreen and the resizable desktop
    // window derive it from the live viewport. The hook reports 0 until its
    // first layout effect, so the resizable panel reads the browser's
    // synchronous dimensions to stay inside the app on its first painted frame.
    const { width: windowWidth, height: windowHeight } = useWindowResize({
      debounce: { wait: 100 },
    });
    const viewportWidth = windowWidth > 0 ? windowWidth : globalThis.innerWidth;
    const viewportHeight = windowHeight > 0 ? windowHeight : globalThis.innerHeight;
    // A remembered size outlives the viewport it was dragged in, so the clamp
    // runs on every render, not just on the gesture.
    const panelSize = clampFloatingAgentPanelSize({
      requestedSize: requestedPanelSize,
      viewportWidth,
      viewportHeight,
    });
    const panelHeight = (() => {
      if (isInAppResizable) return panelSize.height;
      if (isFullscreen && windowHeight > 0) return windowHeight;
      if (fillsDesktopWindow && windowHeight > 0) {
        return windowHeight - PANEL_SHADOW_GUTTER_PX * 2;
      }
      return PANEL_HEIGHT_PX;
    })();

    // Manual window drag over IPC. An -webkit-app-region: drag rect would
    // suppress mouse events over itself, which breaks cursor and hover
    // styling — so the strip tracks the pointer and moves the window itself.
    const handleWindowDragStart = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      windowDragGestureRef.current = {
        pointerId: event.pointerId,
        cursorOffsetX: Math.round(event.clientX),
        cursorOffsetY: Math.round(event.clientY),
        startedAtMs: performance.now(),
      };
    }, []);

    const handleWindowDragEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
      const gesture = windowDragGestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      windowDragGestureRef.current = null;
      void electronModule.floatingAgent.windowDragEnd({
        cursorScreenX: event.screenX,
        cursorScreenY: event.screenY,
        cursorOffsetX: gesture.cursorOffsetX,
        cursorOffsetY: gesture.cursorOffsetY,
      });
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }, []);

    const handleWindowDragMove = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        const gesture = windowDragGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;

        // A pointerup can be lost while the window moves under a captured
        // pointer; a move without a held button means the gesture is over.
        if (event.buttons === 0) {
          logger.warn({
            message: "[FloatingAgentPanel] window drag recovered from a lost pointerup",
            info: {
              durationMs: Math.round(performance.now() - gesture.startedAtMs),
            },
          });
          handleWindowDragEnd(event);
          return;
        }

        electronModule.floatingAgent.windowDragMove({
          cursorScreenX: event.screenX,
          cursorScreenY: event.screenY,
          cursorOffsetX: gesture.cursorOffsetX,
          cursorOffsetY: gesture.cursorOffsetY,
        });
      },
      [handleWindowDragEnd]
    );

    const handleResizeStart = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        resizeGestureRef.current = {
          pointerId: event.pointerId,
          corner: (event.currentTarget.dataset.corner ?? "top-left") as ResizeCorner,
          clientX: event.clientX,
          clientY: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
          size: fillsDesktopWindow
            ? {
                width: Math.max(PANEL_WIDTH_PX, viewportWidth - PANEL_SHADOW_GUTTER_PX * 2),
                height: Math.max(PANEL_HEIGHT_PX, viewportHeight - PANEL_SHADOW_GUTTER_PX * 2),
              }
            : panelSize,
          startedAtMs: performance.now(),
        };
      },
      [fillsDesktopWindow, panelSize, viewportHeight, viewportWidth]
    );

    // Web and docked panels persist once per gesture. The standalone window
    // persists in the main process as its native bounds change.
    const handleResizeEnd = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        const gesture = resizeGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;

        resizeGestureRef.current = null;
        if (!fillsDesktopWindow) {
          localStorageModule.writeMemAgentPanelSize(gesture.size);
        }
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      },
      [fillsDesktopWindow]
    );

    const handleResizeMove = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        const gesture = resizeGestureRef.current;
        if (!gesture || gesture.pointerId !== event.pointerId) return;

        // A pointerup can be lost while the window resizes under a captured
        // pointer; a move without a held button means the gesture is over.
        if (event.buttons === 0) {
          logger.warn({
            message: "[FloatingAgentPanel] resize gesture recovered from a lost pointerup",
            info: {
              corner: gesture.corner,
              width: gesture.size.width,
              height: gesture.size.height,
              durationMs: Math.round(performance.now() - gesture.startedAtMs),
            },
          });
          handleResizeEnd(event);
          return;
        }

        const deltaX = fillsDesktopWindow
          ? event.screenX - gesture.screenX
          : event.clientX - gesture.clientX;
        const deltaY = fillsDesktopWindow
          ? event.screenY - gesture.screenY
          : event.clientY - gesture.clientY;
        // Dragging a left corner outward decreases X; a top corner, Y.
        const signX = gesture.corner.endsWith("left") ? -1 : 1;
        const signY = gesture.corner.startsWith("top") ? -1 : 1;
        const requestedSize = {
          width: gesture.size.width + signX * deltaX,
          height: gesture.size.height + signY * deltaY,
        };
        const nextSize = fillsDesktopWindow
          ? {
              // Screen coordinates can be fractional on scaled displays; the
              // native window API only takes integers.
              width: Math.round(Math.max(PANEL_WIDTH_PX, requestedSize.width)),
              height: Math.round(Math.max(PANEL_HEIGHT_PX, requestedSize.height)),
            }
          : clampFloatingAgentPanelSize({
              requestedSize,
              viewportWidth,
              viewportHeight,
            });
        gesture.clientX = event.clientX;
        gesture.clientY = event.clientY;
        gesture.screenX = event.screenX;
        gesture.screenY = event.screenY;
        gesture.size = nextSize;
        if (fillsDesktopWindow) {
          void electronModule.floatingAgent.resize({
            ...nextSize,
            mode: "panel",
            anchor: RESIZE_CORNER_ANCHORS[gesture.corner],
          });
        } else {
          setRequestedPanelSize(nextSize);
        }
      },
      [fillsDesktopWindow, handleResizeEnd, viewportHeight, viewportWidth]
    );

    // The summon/PTT key needs Accessibility trust; without it the key hook
    // never sees a press, so the only honest surface is a persistent notice.
    const isAccessibilityMissing =
      store.systemPermissions.permissionsAreApplicableForDevice &&
      !store.systemPermissions.hasAccessibilityPermission;

    const openVoicePermissions = useCallback(() => {
      onCollapse();

      if (clientEnvModule.isDesktop()) {
        void Promise.resolve(
          electronModule.floatingAgent.openMemAgentSettings({
            permission: "voice",
          })
        ).catch((unknownError: unknown) => {
          const error = unknownError as Error;
          logger.warn({
            message: "[FloatingAgentPanel] Failed to open voice permissions in main window",
            info: { error: objectModule.safeErrorAsJson(error) },
          });
        });
        return;
      }

      store.systemPermissions.ui.openVoicePermissionsModal();
    }, [onCollapse, store.systemPermissions.ui]);

    const openMicrophoneSettings = useCallback(() => {
      void electronModule.recall.openPermissionPane({
        permission: RecallPermissionKind.Microphone,
      });
    }, []);

    // Root events (windowed liveQuery, thread replies excluded — those render
    // in the thread pane).
    const rootEvents = floatingAgentPage.rootEvents;
    const threadSummaries = floatingAgentPage.threadSummaryByRootEventId;
    const sessionStatus = floatingAgentPage.session?.modelData?.status;
    const holdToTalkKey = store.spaceAccountSettings.memAgent.holdToTalkKey;
    const resolvedHoldToTalkKey = holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey();
    const holdToTalkDisplay = store.spaceAccountSettings.memAgent.holdToTalkIsEnabled
      ? getMemAgentHoldToTalkKeyDisplay(resolvedHoldToTalkKey)
      : null;
    const shouldShowUpgradeButton = shouldShowFloatingAgentUpgradeButton({
      hasProactiveAccess: store.subscriptions.hasProactiveAccess,
    });
    const panelContentHeight = panelHeight - PANEL_HEADER_HEIGHT_PX - PANEL_HEADER_GAP_PX;

    useEffect(() => {
      sessionRef.current?.upsertEvents(
        exportFloatingAgentSessionEvents(rootEvents, { threadSummaries })
      );
    }, [rootEvents, threadSummaries]);

    // Session status arrives via sync (BE-written per exchange); no optimistic
    // thinking state.
    useEffect(() => {
      if (sessionStatus) {
        sessionRef.current?.updateStatus(sessionStatus as SessionStatus);
      }
    }, [sessionStatus]);

    // Summon opens the panel ready to type when quota allows it. Not on phone
    // web (fullscreen), where focusing would pop the on-screen keyboard.
    useEffect(() => {
      if (isFullscreen) return;
      if (floatingAgentPage.isNativeChatMessageInputDisabled) return;
      sessionRef.current?.focusComboboxInput();
    }, [isFullscreen, floatingAgentPage, floatingAgentPage.isNativeChatMessageInputDisabled]);

    const { comboboxProps, clearMentions, buildContext } = useComposerMentions({
      externalMentions: foregroundMentions,
      pendingMentions: store.floatingAgent.pendingComposerMentions,
      pendingMentionsRequestId: store.floatingAgent.pendingComposerMentionsRequestId,
      onMentionsAttached: () => store.floatingAgent.consumeComposerMentions(),
    });

    // Register this pane's chips for PTT sends (see composerRegistryRef).
    useEffect(() => {
      const composerRegistry = composerRegistryRef.current;
      composerRegistry.main = { buildContext, clearMentions };
      return () => {
        composerRegistry.main = null;
      };
    }, [composerRegistryRef, buildContext, clearMentions]);

    const handleNativeChatLimitClick = useCallback(() => {
      onCollapse();
      void floatingAgentPage.openNativeChatLimitUpsellForCurrentGate();
    }, [floatingAgentPage, onCollapse]);

    const openDesktopDownload = useCallback(() => {
      const target = isWin() ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP;
      windowModule.openInNewTab({ url: new URL(target) });
    }, []);
    const openUpgradeUpsell = useCallback(async () => {
      if (isOpeningUpgrade) return;

      // The paywall opens elsewhere (main renderer on desktop, modal layer on
      // web), so the panel gets out of its way before the open resolves.
      onCollapse();
      setIsOpeningUpgrade(true);
      try {
        await floatingAgentPage.openProactiveUpsell();
      } catch (unknownError) {
        const error = unknownError as Error;
        logger.error({
          message: "[FloatingAgentPanel] Failed to open Proactive upsell",
          info: { error: objectModule.safeErrorAsJson(error) },
        });
        toastModule.triggerToast({
          content: "Couldn't open upgrade options. Please try again.",
        });
      } finally {
        setIsOpeningUpgrade(false);
      }
    }, [isOpeningUpgrade, floatingAgentPage, onCollapse]);

    const openMemAgentSettings = useCallback(
      (channel?: FloatingAgentMemAgentSettingsActionChannel) => {
        onCollapse();

        if (clientEnvModule.isDesktop()) {
          void Promise.resolve(
            electronModule.floatingAgent.openMemAgentSettings({
              ...(channel ? { channel } : {}),
            })
          ).catch((unknownError: unknown) => {
            const error = unknownError as Error;
            logger.warn({
              message: "[FloatingAgentPanel] Failed to open Mem Agent settings in main window",
              info: { error: objectModule.safeErrorAsJson(error) },
            });
          });
          return;
        }

        if (channel) {
          store.settingsModal.openMemAgentCommunicationChannels(channel);
          return;
        }

        store.settingsModal.open(SettingsTabId.MemAgentPage);
      },
      [onCollapse, store.settingsModal]
    );

    const settingsMenuContent = useMemo<MDSMenuContentList>(
      () => [
        {
          id: "connect-on-other-apps",
          kind: MDSMenuContentItemKind.Header,
          label: "Connect on other apps",
        },
        {
          id: "connect-imessage",
          kind: MDSMenuContentItemKind.Item,
          label: store.memAgent.isIMessageConnected ? "iMessage connected" : "Connect iMessage",
          icon: MDSIconKind.Comment,
          isDisabled: store.memAgent.isIMessageConnected,
          onClick: () => openMemAgentSettings("imessage"),
        },
        {
          id: "connect-whatsapp",
          kind: MDSMenuContentItemKind.Item,
          label: store.memAgent.isWhatsAppConnected ? "WhatsApp connected" : "Connect WhatsApp",
          icon: MDSIconKind.WhatsApp,
          isDisabled: store.memAgent.isWhatsAppConnected,
          onClick: () => openMemAgentSettings("whatsapp"),
        },
        {
          id: "connect-slack",
          kind: MDSMenuContentItemKind.Item,
          label: store.memAgent.isSlackConnected ? "Open Slack" : "Connect Slack",
          icon: MDSIconKind.Slack,
          isDisabled: store.memAgent.isConnectingSlack,
          onClick: () => openMemAgentSettings("slack"),
        },
        {
          id: "mem-agent-settings",
          kind: MDSMenuContentItemKind.Item,
          label: "Mem Agent settings",
          icon: MDSIconKind.Settings,
          onClick: () => openMemAgentSettings(),
        },
      ],
      [
        openMemAgentSettings,
        store.memAgent.isIMessageConnected,
        store.memAgent.isSlackConnected,
        store.memAgent.isConnectingSlack,
        store.memAgent.isWhatsAppConnected,
      ]
    );

    // A tapped main-list peek bubble lands ON its message: the target rides
    // into the session config, positioning the list's first render and
    // anchoring the "New" divider. Engaging the composer retires the divider.
    const initialScrollToEventId =
      openTarget && !openTarget.threadRootId ? openTarget.eventId : undefined;
    const mainUnreadDivider = useUnreadDividerDismissal(initialScrollToEventId);
    const retireMainUnreadDivider = mainUnreadDivider.retireDivider;

    // A capture in any of its live states swaps the composer's controls for
    // the voice row; idle leaves the composer untouched.
    const isCapturing =
      voice.state === "pending" ||
      voice.state === "starting" ||
      voice.isListening ||
      voice.state === "finalizing";

    const sessionComboboxProps = useMemo(
      () => ({
        ...comboboxProps,
        isDisabled: floatingAgentPage.isNativeChatMessageInputDisabled,
        onDisabledClick: handleNativeChatLimitClick,
        setContent: composerDraftHtml ?? undefined,
        onContentHtmlChanged: handleComposerContentHtmlChanged,
        // Typing, opening the mention picker, and adding a chip all count as
        // starting a reply.
        onContentModified: retireMainUnreadDivider,
        onLeftAddonButtonClick: retireMainUnreadDivider,
        onMentionAdd: (mention: MDSComboboxMention) => {
          retireMainUnreadDivider();
          comboboxProps.onMentionAdd(mention);
        },
        voice: isCapturing
          ? {
              amplitude: voice.amplitude,
              isFinalizing: voice.state === "finalizing",
              onCancel: onCancelVoice,
              onConfirm: onConfirmVoice,
            }
          : undefined,
      }),
      [
        comboboxProps,
        floatingAgentPage.isNativeChatMessageInputDisabled,
        handleNativeChatLimitClick,
        composerDraftHtml,
        handleComposerContentHtmlChanged,
        retireMainUnreadDivider,
        isCapturing,
        voice.amplitude,
        voice.state,
        onCancelVoice,
        onConfirmVoice,
      ]
    );

    const handleInputFormSubmit = useCallback(
      async ({
        content,
        context,
      }: {
        content: string;
        context: MDSComboboxFetchDataHandlerSessionInputFormContext;
      }) => {
        const sent = await floatingAgentPage.sendUserMessage({
          content,
          source: "text",
          context,
        });
        if (sent) {
          clearMentions();
          // The editor's own post-send clear also reports empty content; this
          // covers the window dying before that notification lands.
          composerHasSeenContentRef.current = false;
          lastPersistedComposerDraftRef.current = null;
          localStorageModule.clearMemAgentComposerDraft();
        }
        return sent;
      },
      [floatingAgentPage, clearMentions]
    );

    const handleRetry = useCallback(async () => {
      sessionRef.current?.clearSessionError();
      await floatingAgentPage.retryExchange();
    }, [floatingAgentPage]);

    // Slack-style threads: opening one slides the thread pane over the main
    // list; back slides home. "Start a thread", "N replies", and a tapped
    // thread-reply peek bubble all land here.
    const [openThreadRootId, setOpenThreadRootId] = useState<string | null>(
      openTarget?.threadRootId ?? null
    );

    const threadScrollToEventId = useLatchedThreadUnreadAnchor({
      openThreadRootId,
      openTarget,
      floatingAgentPage,
    });
    const threadUnreadDivider = useUnreadDividerDismissal(threadScrollToEventId);

    return (
      <StyledPanel
        data-component="FloatingAgentPanel"
        $resizable={isInAppResizable}
        $fullscreen={isFullscreen}
        $fillsWindow={fillsDesktopWindow}
        style={
          isInAppResizable
            ? {
                width: panelSize.width,
                height: panelSize.height,
              }
            : undefined
        }
      >
        <StyledPanelHeader>
          {fillsDesktopWindow && (
            <StyledHeaderDragStrip
              aria-hidden
              onPointerDown={handleWindowDragStart}
              onPointerMove={handleWindowDragMove}
              onPointerUp={handleWindowDragEnd}
              onPointerCancel={handleWindowDragEnd}
            />
          )}
          {openThreadRootId !== null ? (
            <StyledThreadBackButton
              aria-label="Back to conversation"
              onClick={() => setOpenThreadRootId(null)}
            >
              <MDSIcon kind={MDSIconKind.AngleLeft} size={14} boxSize={14} />
              <MDSText
                variant={MDSTextVariant.TextM}
                weight={MDSTextWeight.Smedium}
                color={MDSTextColor.Contrast}
              >
                Thread
              </MDSText>
            </StyledThreadBackButton>
          ) : (
            <StyledPanelTitle>
              <StyledPanelIdentity>
                <StyledPanelTitleLogo kind={MDSLogoKind.ArrowsCropped} />
                <MDSText
                  variant={MDSTextVariant.TextM}
                  weight={MDSTextWeight.Smedium}
                  color={MDSTextColor.Contrast}
                >
                  Mem
                </MDSText>
              </StyledPanelIdentity>
              <FloatingAgentConnectedWorkLink onCollapse={onCollapse} />
            </StyledPanelTitle>
          )}
          <StyledPanelHeaderActions>
            {shouldShowUpgradeButton && (
              <MDSButton
                aria-label="Upgrade to Mem Proactive"
                variant={MDSButtonVariant.Emphasis}
                size={MDSButtonSize.Small}
                isDisabled={isOpeningUpgrade}
                onClick={openUpgradeUpsell}
              >
                Upgrade
              </MDSButton>
            )}
            <MDSActionMenuDropdown
              contentList={settingsMenuContent}
              isOpen={settingsMenuIsOpen}
              onOpenChange={setSettingsMenuIsOpen}
              placement="bottom right"
              popoverProps={{ crossOffset: SETTINGS_MENU_PANEL_EDGE_CROSS_OFFSET_PX }}
            >
              <StyledSettingsMenuButton
                slot={MDSMenuDropdownSlotName.Trigger}
                aria-label="Mem Agent actions"
                aria-pressed={settingsMenuIsOpen}
                $isMenuOpen={settingsMenuIsOpen}
                iconKind={MDSIconKind.Settings}
                variant={MDSIconButtonVariant.Transparent}
              />
            </MDSActionMenuDropdown>
            <MDSIconButton
              aria-label="Minimize chat"
              iconKind={collapseIconKind}
              variant={MDSIconButtonVariant.Transparent}
              onClick={onCollapse}
            />
          </StyledPanelHeaderActions>
        </StyledPanelHeader>
        {showsResizeHandle &&
          RESIZE_CORNERS.map(corner => (
            <StyledResizeHandle
              key={corner}
              type="button"
              aria-label={corner === "top-left" ? "Resize Mem Agent" : `Resize Mem Agent ${corner}`}
              title="Drag to resize"
              data-corner={corner}
              $corner={corner}
              onPointerDown={handleResizeStart}
              onPointerMove={handleResizeMove}
              onPointerUp={handleResizeEnd}
              onPointerCancel={handleResizeEnd}
            />
          ))}
        {isAccessibilityMissing && (
          <StyledNoticeBar>
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              Voice needs the Accessibility permission to hear the talk key.
            </MDSText>
            <StyledNoticeAction onClick={openVoicePermissions}>
              Open Settings
            </StyledNoticeAction>
          </StyledNoticeBar>
        )}
        {voiceNotice && (
          <StyledNoticeBar>
            <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {voiceNoticeCopy(voiceNotice)}
            </MDSText>
            {/* The settings shortcut opens a macOS permission pane — only
                the desktop shell can do that. */}
            {voiceNotice === "mic-denied" && clientEnvModule.isDesktop() && (
              <StyledNoticeAction onClick={openMicrophoneSettings}>
                Open Settings
              </StyledNoticeAction>
            )}
            <StyledNoticeAction onClick={onDismissVoiceNotice}>Dismiss</StyledNoticeAction>
          </StyledNoticeBar>
        )}
        <StyledPanelBody>
          <StyledSlideTrack $threadOpen={openThreadRootId !== null}>
            <StyledSlidePane>
              <MDSSession
                ref={sessionRef}
                sessionTitle="Mem"
                sessionSubtitle=""
                initialSessionEvents={[]}
                initialSessionStatus={(sessionStatus as SessionStatus) || SessionStatus.Initial}
                containerHeight={panelContentHeight}
                isOpen
                comboboxProps={sessionComboboxProps}
                config={{
                  openLinksInNewTab: false,
                  features: [SessionFeature.AllowCopy],
                  linkHandler: handleMentionLink,
                  onClickLink: handleClickLink,
                  // One immortal chat — no chat switches to smooth over.
                  disableListEnterAnimation: true,
                  initialScrollToEventId,
                  // Same latched target, until the composer is engaged: the
                  // divider marks where the unread messages the open landed on
                  // begin, and holds that spot even as replies are marked seen.
                  unreadDividerEventId: mainUnreadDivider.unreadDividerEventId,
                  isUnreadDividerRetired: mainUnreadDivider.isUnreadDividerRetired,
                  onCaughtUpNearBottom: retireMainUnreadDivider,
                  showJumpToLatest: true,
                  // Counted from the divider's own latched anchor, so the two
                  // always agree. Stops once the divider retires: the user has
                  // caught up, so there is nothing left to count.
                  unreadCount: mainUnreadDivider.isUnreadDividerRetired
                    ? 0
                    : floatingAgentPage.unreadCountFromAnchor(
                        mainUnreadDivider.unreadDividerEventId
                      ),
                  // No autofocus on phone web: opening the chat should not
                  // pop the on-screen keyboard.
                  autoFocusComposer:
                    !isFullscreen && !floatingAgentPage.isNativeChatMessageInputDisabled,
                  onScrolledNearTop: () => floatingAgentPage.loadOlderEvents(),
                  // Chrome below the list (composer padding + list margin, plus
                  // the PTT hint row); the combobox is measured separately. The
                  // header is already excluded from panelContentHeight, so it
                  // must NOT be counted here too — doing so shrank the list by a
                  // header's height.
                  staticViewChromeHeightPx: showComposerHint
                    ? 16 + PANEL_COMPOSER_HINT_HEIGHT_PX
                    : 16,
                  // Fullscreen: StyledPanel shrinks its content box by the
                  // safe-area inset and narrow-edge margin, so the list math
                  // must subtract the same inset or the composer clips under
                  // the home indicator. The header and its gap come off too —
                  // the non-fullscreen basis is panelContentHeight, which
                  // already excludes them, and staticViewChromeHeightPx is
                  // written to match that. Leaving them in made the list
                  // overrun the composer by a header's height and hid the
                  // streaming "Thinking..." row behind it.
                  staticViewHeightBasis: isFullscreen
                    ? `calc(100dvh - env(safe-area-inset-bottom) - ${
                        PANEL_HEADER_HEIGHT_PX +
                        PANEL_HEADER_GAP_PX +
                        FULLSCREEN_PANEL_MARGIN_PX * 2
                      }px)`
                    : `${panelContentHeight}px`,
                  // Matches StyledPanel's background so messages fade into the
                  // panel behind the composer instead of ending on a hard edge.
                  staticViewComposerFadeColor: theme.colors.surface.chat,
                  threads: {
                    onStartThread: setOpenThreadRootId,
                    onOpenThread: setOpenThreadRootId,
                    userPhotoUrl: store.account.myAccount.profilePhotoUrl,
                  },
                }}
                handlers={{
                  handleViewModeChanged: () => {},
                  handleCountdownCompleted: () => {},
                  handleFetchComboboxData: floatingAgentPage.handleComboboxFetch,
                  handleInputFormSubmit,
                  handleRetry,
                }}
              />
              {showComposerHint && (
                <StyledComposerHint>
                  {clientEnvModule.isDesktop() && holdToTalkDisplay ? (
                    <>
                      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                        {holdToTalkDisplay.sentencePrefix
                          ? `Hold ${holdToTalkDisplay.sentencePrefix}`
                          : "Hold"}
                      </MDSText>
                      <StyledComposerHintKey aria-label={holdToTalkDisplay.accessibleLabel}>
                        {holdToTalkDisplay.tooltipKey}
                      </StyledComposerHintKey>
                      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                        to talk · double-tap for hands-free
                      </MDSText>
                    </>
                  ) : clientEnvModule.isDesktop() ? (
                    <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                      Enable Push-to-Remember in settings to use the keyboard shortcut
                    </MDSText>
                  ) : (
                    // No PTT on web — voice needs the desktop app's key hook.
                    <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                      <StyledComposerHintLink
                        variant={MDSTextVariant.TextXs}
                        onPress={openDesktopDownload}
                      >
                        Download
                      </StyledComposerHintLink>{" "}
                      the desktop app for push to talk.
                    </MDSText>
                  )}
                </StyledComposerHint>
              )}
            </StyledSlidePane>
            <StyledSlidePane>
              {openThreadRootId && (
                <FloatingAgentThreadView
                  floatingAgentPage={floatingAgentPage}
                  rootEventId={openThreadRootId}
                  containerHeight={panelContentHeight}
                  composerRegistryRef={composerRegistryRef}
                  foregroundMentions={foregroundMentions}
                  scrollToEventId={threadScrollToEventId}
                  unreadDividerEventId={threadUnreadDivider.unreadDividerEventId}
                  isUnreadDividerRetired={threadUnreadDivider.isUnreadDividerRetired}
                  onRetireUnreadDivider={threadUnreadDivider.retireDivider}
                  autoFocusComposer={!isFullscreen}
                />
              )}
            </StyledSlidePane>
          </StyledSlideTrack>
        </StyledPanelBody>
      </StyledPanel>
    );
  }
);

const StyledPanel = styled.div<{
  $resizable: boolean;
  $fullscreen: boolean;
  $fillsWindow: boolean;
}>`
  position: relative;
  width: ${PANEL_WIDTH_PX}px;
  height: ${PANEL_HEIGHT_PX}px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  /* The chat surface — surface.popover matches the user-message bubble
     color and the bubbles vanish against it. */
  background: ${({ theme }) => theme.colors.surface.chat};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  /* Enter from the anchor corner once expandToPanel reveals the window. */
  transform-origin: var(--floating-agent-enter-origin, bottom right);
  animation: floating-agent-panel-enter 160ms cubic-bezier(0.16, 1, 0.3, 1) both;

  ${({ $fillsWindow }) =>
    $fillsWindow &&
    `
    width: calc(100vw - ${PANEL_SHADOW_GUTTER_PX * 2}px);
    height: calc(100vh - ${PANEL_SHADOW_GUTTER_PX * 2}px);
    margin: ${PANEL_SHADOW_GUTTER_PX}px;
  `}

  ${({ $resizable }) =>
    $resizable &&
    `
    max-width: calc(100vw - ${PANEL_VIEWPORT_MARGIN_PX * 2}px);
    max-height: calc(100dvh - ${PANEL_VIEWPORT_MARGIN_PX * 2}px);
  `}

  /* Phone sheet: the whole viewport, entering as a slide-up instead of a
     corner scale. The safe-area padding keeps the composer above the iOS
     home indicator. */
  ${({ $fullscreen }) =>
    $fullscreen &&
    `
    width: calc(100% - ${FULLSCREEN_PANEL_MARGIN_PX * 2}px);
    height: calc(100dvh - ${FULLSCREEN_PANEL_MARGIN_PX * 2}px);
    margin: ${FULLSCREEN_PANEL_MARGIN_PX}px;
    border: none;
    box-shadow: none;
    padding-bottom: env(safe-area-inset-bottom);
    animation: floating-agent-panel-enter-sheet 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  `}

  @keyframes floating-agent-panel-enter {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes floating-agent-panel-enter-sheet {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const RESIZE_HANDLE_SIZE_PX = 20;
/* The header's top padding row — grabbing there never collides with the
   title or the action buttons. */
const DRAG_STRIP_HEIGHT_PX = 12;

const RESIZE_CORNERS = ["top-left", "top-right", "bottom-left", "bottom-right"] as const;
type ResizeCorner = (typeof RESIZE_CORNERS)[number];

/** Corner kept pinned while the opposite corner is dragged. */
const RESIZE_CORNER_ANCHORS: Record<ResizeCorner, FloatingAgentWindowAnchor> = {
  "top-left": FloatingAgentWindowAnchor.BottomRight,
  "top-right": FloatingAgentWindowAnchor.BottomLeft,
  "bottom-left": FloatingAgentWindowAnchor.TopRight,
  "bottom-right": FloatingAgentWindowAnchor.TopLeft,
};

const RESIZE_CORNER_STYLES: Record<ResizeCorner, string> = {
  "top-left": "top: 0; left: 0; cursor: nwse-resize;",
  "top-right": "top: 0; right: 0; cursor: nesw-resize;",
  "bottom-left": "bottom: 0; left: 0; cursor: nesw-resize;",
  "bottom-right": "bottom: 0; right: 0; cursor: nwse-resize;",
};

const StyledResizeHandle = styled.button<{ $corner: ResizeCorner }>`
  position: absolute;
  z-index: 10000;
  ${({ $corner }) => RESIZE_CORNER_STYLES[$corner]}
  width: ${RESIZE_HANDLE_SIZE_PX}px;
  height: ${RESIZE_HANDLE_SIZE_PX}px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  touch-action: none;
  -webkit-app-region: no-drag;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.default};
    outline-offset: -2px;
  }
`;

const StyledPanelHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: ${PANEL_HEADER_HEIGHT_PX}px;
  flex: 0 0 ${PANEL_HEADER_HEIGHT_PX}px;
  user-select: none;
  -webkit-app-region: no-drag;
  /* Side inset matches the message list and composer (spacing[5]) so the
     logomark, agent messages, and the input all sit on one vertical line.
     Symmetric 8px padding centres the title, Upgrade, and minimize on the
     40px control row. */
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
`;

/* Window-drag surface for the standalone desktop window: only the thin band
   above the header content, so the cursor stays consistent over the header
   row. Inset from both ends so the top resize corners never overlap it.
   Pointer-driven — no app-region, so cursor and hover styling stay
   reliable. */
const StyledHeaderDragStrip = styled.div`
  position: absolute;
  top: 0;
  height: ${DRAG_STRIP_HEIGHT_PX}px;
  left: ${RESIZE_HANDLE_SIZE_PX}px;
  right: ${RESIZE_HANDLE_SIZE_PX}px;
  -webkit-app-region: no-drag;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

/* Logomark + wordmark as one unit on the header's left, per the mock.
   Raised above the drag strip so it stays hit-testable. */
const StyledPanelTitle = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  min-width: 0;
  flex: 1 1 auto;
`;

const StyledPanelIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  flex: 0 0 auto;
`;

/* The mock's mark is 16px wide; MDSLogo drives height with width:auto, and the
   cropped-arrows art is 43:24, so 9px of height lands on that width. */
const StyledPanelTitleLogo = styled(MDSLogo)`
  height: 9px;
`;

/* Groups the trailing controls so the header stays a two-child space-between:
   title on the left, this cluster on the right. The header is the window's
   drag handle, so its interactive children opt back out. */
const StyledPanelHeaderActions = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  -webkit-app-region: no-drag;
`;

const StyledSettingsMenuButton = styled(MDSIconButton, {
  shouldForwardProp: prop => prop !== "$isMenuOpen",
})<{ $isMenuOpen: boolean }>`
  && {
    ${({ $isMenuOpen, theme }) =>
      $isMenuOpen && `background-color: ${theme.colors.background.hoverOverlay.default};`}
  }

  &&[data-hovered]:not([data-disabled]),
  &&:hover:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.hoverOverlay.default};
  }
`;

/* Icon + label as ONE hit target, flush left so the icon aligns with the
   composer edge. */
const StyledThreadBackButton = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  padding: 4px 6px 4px 0;
  -webkit-app-region: no-drag;
`;

const StyledPanelBody = styled.div`
  position: relative;
  /* Clear space between the header row and the first message. */
  padding-top: ${PANEL_HEADER_GAP_PX}px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledSlideTrack = styled.div<{ $threadOpen: boolean }>`
  display: flex;
  width: 200%;
  flex: 1;
  min-height: 0;
  transform: translateX(${({ $threadOpen }) => ($threadOpen ? "-50%" : "0")});
  transition: transform ${PANEL_THREAD_SLIDE_MS}ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const StyledSlidePane = styled.div`
  width: 50%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* PTT-discovery hint centered under the composer; the margins add up to
   PANEL_COMPOSER_HINT_HEIGHT_PX so the chrome-height math stays honest. */
const StyledComposerHint = styled.div`
  height: 16px;
  margin: -12px 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  /* The composer section above is position:absolute bottom:0 with 20px
     padding-bottom, so its box overhangs this hint and would swallow the
     Download link's clicks. Lift the hint into its own stacking context. */
  position: relative;
  z-index: 1;
`;

/* The download link matches the hint text (text.secondary) instead of the
   default link blue — it reads as an inline affordance, keeping the underline. */
const StyledComposerHintLink = styled(MDSLink)`
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover:not([data-disabled]) {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const StyledComposerHintKey = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 ${({ theme }) => theme.spacing["1"]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  line-height: 1;
  font-weight: 500;
  white-space: nowrap;
`;

const StyledNoticeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.ghost};
`;

const StyledNoticeAction = styled.button`
  border: none;
  background: transparent;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.link.default};
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
`;
